import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import {
  EvaluationTypeLabelsEnum,
  StandardDeviationsTypes,
  DocumentTypes,
} from '@/app/utils/constants';
import { SearchFocusIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { StandardDeviationsDetailPageContent } from '@/app/components/features/evaluations/StandardDeviations/StandardDeviationsDetailPageContent';
import { useGetStandardDeviations } from '@/app/components/features/evaluations/analysis/Deviations/useDeviations';
import { groupDeviations } from '@/app/components/features/evaluations/analysis/Deviations/groupingUtils';
import { flattenStandardDeviationStatements } from '@/app/components/features/evaluations/analysis/Deviations/utils';
import { StandardDeviation } from '@/app/types/models';
import { useOrganizationKnowledgeBases } from '@/app/hooks/queries/useInternalKnowledgeBases';
import useDocumentsQuery from '@/app/components/shared/EntityDocuments/useDocumentsQuery';
import { useStandardDocumentNames } from '@/app/components/features/evaluations/analysis/Deviations/useStandardDocumentNames';
import type { DeviationsFlatRow } from '@/app/components/features/evaluations/StandardDeviations/StandardDeviationsPageContent';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';
import { use } from 'react';
import { AppContext } from '@/app/appContext';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/standard-deviations/$deviationId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tFeature = useTranslations('features.standardDeviations');
  const { tenderPackageId, projectId, deviationId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { userMeta } = use(AppContext);
  const organizationId = userMeta?.organizationId || '';

  const { data: contractStandardKbs } = useOrganizationKnowledgeBases(organizationId);

  const kbById = useMemo(() => {
    const m = new Map<string, string>();
    for (const kb of contractStandardKbs ?? []) m.set(kb.id, kb.name);
    return m;
  }, [contractStandardKbs]);

  const { data } = useGetStandardDeviations({
    deviationsType: StandardDeviationsTypes.TENDER_PACKAGE,
    id: tenderPackageId,
    enabled: true,
  });
  const items = data?.items;

  const { data: documentsData } = useDocumentsQuery({
    documentsType: DocumentTypes.TENDER_PACKAGE,
    id: tenderPackageId,
    orderBy: 'CreatedAt',
    orderDirection: 'desc',
  });

  const flatStatements = useMemo(
    () => flattenStandardDeviationStatements(items as StandardDeviation[]),
    [items],
  );

  const { standardDocNameMap } = useStandardDocumentNames(flatStatements);

  const statementsWithDocNames = useMemo(() => {
    return flatStatements.map((statement) => {
      const evalDoc = documentsData?.find((doc) => doc.id === statement.evalDocId);
      const standardDocName = standardDocNameMap.get(statement.standardDocId);

      return {
        ...statement,
        evalDocName: evalDoc?.name || statement.evalDocName || '',
        standardDocName: standardDocName || statement.standardDocName || '',
      };
    });
  }, [flatStatements, documentsData, standardDocNameMap]);

  const allRows: DeviationsFlatRow[] = useMemo(() => {
    const rows: DeviationsFlatRow[] = [];
    let idx = 0;
    for (const st of statementsWithDocNames) {
      const kbId = st.standardEvalId;
      if (!kbId || !kbById.has(kbId)) {
        continue;
      }
      const standardName = kbById.get(kbId) ?? '';
      rows.push({
        index: idx++,
        statement: st.statement,
        standardName,
        kbId,
        item: st,
      });
    }
    return rows;
  }, [statementsWithDocNames, kbById]);

  const groupedDeviations = useMemo(() => groupDeviations(allRows, 'standards'), [allRows]);

  const allGroups = useMemo(
    () =>
      groupedDeviations.map((group, index) => ({
        id: String(index),
        group,
      })),
    [groupedDeviations],
  );

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allGroups, deviationId, (deviationId) => ({
      to: '/project/$projectId/tender-packages/$tenderPackageId/standard-deviations/$deviationId',
      params: { projectId, tenderPackageId, deviationId },
    }));

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/standard-deviations"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/standard-deviations"
                params={{ projectId, tenderPackageId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <StandardDeviationsDetailPageContent
        evaluationId={tenderPackageId}
        evaluationType={StandardDeviationsTypes.TENDER_PACKAGE}
        documentType={DocumentTypes.TENDER_PACKAGE}
        deviationId={deviationId}
      />
    </>
  );
}
